import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase, supabaseAnonKey, supabaseUrl } from "@/lib/supabase";

export const OWNER_DEMO_FALLBACK_URL =
  "https://calendly.com/coliviq/demo-call-coliviq-fur-ihre-immobilie";

export type OwnerDemoBooking = {
  bookedAt: string | null;
  eventUri: string | null;
  inviteeUri: string | null;
  inviteeEmail: string | null;
  startTime: string;
  endTime: string;
  cancelUrl: string | null;
  rescheduleUrl: string | null;
  status: "active";
};

export class OwnerDemoApiError extends Error {
  status: number;
  reason?: string;
  fallbackUrl?: string;

  constructor(
    message: string,
    status: number,
    reason?: string,
    fallbackUrl?: string
  ) {
    super(message);
    this.name = "OwnerDemoApiError";
    this.status = status;
    this.reason = reason;
    this.fallbackUrl = fallbackUrl;
  }
}

async function callOwnerDemo<T>(body: Record<string, unknown>): Promise<T> {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new OwnerDemoApiError("Authentication required", 401);
  }

  const response = await fetch(`${supabaseUrl}/functions/v1/calendly-demo`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${session.access_token}`,
      apikey: supabaseAnonKey,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(body),
  });
  const payload = (await response.json().catch(() => ({}))) as {
    error?: string;
    reason?: string;
    fallbackUrl?: string;
  };
  if (!response.ok) {
    throw new OwnerDemoApiError(
      payload.error ?? "Demo request failed",
      response.status,
      payload.reason,
      payload.fallbackUrl
    );
  }
  return payload as T;
}

// While the panel is open the owner is often cancelling or rescheduling in a
// Calendly tab. The webhook writes the change within a second, so poll at a
// pace that feels immediate without being chatty; react-query pauses this
// while the tab is hidden, and the query is only live while the panel is.
export const OWNER_DEMO_POLL_MS = 15_000;

export function useOwnerDemoStatus(enabled: boolean, live = false) {
  return useQuery({
    queryKey: ["owner-demo", "status"],
    queryFn: () =>
      callOwnerDemo<{
        booking: OwnerDemoBooking | null;
        fallbackUrl: string;
      }>({ action: "status" }),
    enabled,
    // Cancelling and rescheduling happen on Calendly, in another tab, and
    // reach us only through the webhook. Holding this fresh for a minute
    // meant an owner came back to a panel still showing the old appointment,
    // so let the window-focus refetch pick the change up immediately.
    staleTime: 0,
    refetchInterval: live ? OWNER_DEMO_POLL_MS : false,
    retry: 1,
  });
}

export function useDemoAvailability(
  startTime: string,
  endTime: string,
  enabled: boolean
) {
  return useQuery({
    queryKey: ["owner-demo", "availability", startTime, endTime],
    queryFn: () =>
      callOwnerDemo<{
        slots: Array<{ startTime: string }>;
        timezone: string;
      }>({ action: "availability", startTime, endTime }),
    enabled,
    staleTime: 60_000,
    retry: 1,
  });
}

export function useBookDemo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      startTime: string;
      name: string;
      email: string;
      note: string;
    }) =>
      callOwnerDemo<{ booking: OwnerDemoBooking }>({
        action: "book",
        ...input,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(["owner-demo", "status"], {
        booking: data.booking,
        fallbackUrl: OWNER_DEMO_FALLBACK_URL,
      });
      void queryClient.invalidateQueries({
        queryKey: ["owner-demo", "availability"],
      });
    },
  });
}
